// supabase.js
import { supabase } from "../utils/supabase";

export const subscribeToTasks = (cb) => {
  try {
    console.log("✅ SUPABASE SERVICE: Setting up task subscription");
    const subscription = supabase
      .channel("public:tasks")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tasks" },
        (payload) => {
          console.log(
            "✅ SUPABASE SERVICE: Received real-time update",
            payload
          );
          cb(payload);
        }
      )
      .subscribe();

    return () => {
      console.log("✅ SUPABASE SERVICE: Cleaning up task subscription");
      supabase.removeChannel(subscription);
    };
  } catch (error) {
    console.error("❌ SUPABASE SERVICE: Error subscribing to tasks:", error);
    return () => {};
  }
};

export const updateTask = async (id, updated) => {
  console.log(`✅ SUPABASE SERVICE: Updating task ${id} with:`, updated);
  const { data, error } = await supabase
    .from("tasks")
    .update(updated)
    .eq("id", id)
    .select();

  if (error) {
    console.error("❌ SUPABASE SERVICE: Error updating task:", error);
    return null;
  }

  console.log("✅ SUPABASE SERVICE: Task updated successfully:", data[0]);
  return data[0];
};

export const fetchTasks = async () => {
  console.log("✅ SUPABASE SERVICE: Fetching all tasks");
  const { data, error } = await supabase
    .from("tasks")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("❌ SUPABASE SERVICE: Error fetching tasks:", error);
    return [];
  }

  console.log(`✅ SUPABASE SERVICE: Fetched ${data.length} tasks successfully`);
  return data;
};

export const insertTask = async (task) => {
  console.log("✅ SUPABASE SERVICE: Inserting new task:", task);
  const { data, error } = await supabase.from("tasks").insert([task]).select();

  if (error) {
    console.error("❌ SUPABASE SERVICE: Error inserting task:", error);
    return null;
  }

  console.log("✅ SUPABASE SERVICE: Task inserted successfully:", data[0]);
  return data[0];
};

export async function uploadFile(file, bucket = "task-attachments") {
  console.log(
    `✅ SUPABASE SERVICE: Uploading file "${file.name}" to bucket "${bucket}"`
  );
  const fileExt = file.name.split(".").pop();
  const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
  const filePath = `${fileName}`;

  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(filePath, file);

  if (error) {
    console.error("❌ SUPABASE SERVICE: Error uploading file:", error);
    return null;
  }

  const { data: urlData } = supabase.storage
    .from(bucket)
    .getPublicUrl(filePath);

  console.log(
    "✅ SUPABASE SERVICE: File uploaded successfully:",
    urlData.publicUrl
  );
  return urlData.publicUrl;
}

export async function uploadMultipleFiles(files, bucket = "task-attachments") {
  console.log(
    `✅ SUPABASE SERVICE: Uploading ${files.length} files to bucket "${bucket}"`
  );

  const uploadPromises = Array.from(files).map((file) =>
    uploadFile(file, bucket)
  );
  const results = await Promise.all(uploadPromises);

  // Filter out any failed uploads (null results)
  const successfulUploads = results.filter(Boolean);
  console.log(
    `✅ SUPABASE SERVICE: Successfully uploaded ${successfulUploads.length}/${files.length} files`
  );

  return successfulUploads;
}

export async function insertTaskAttachments(taskId, files) {
  if (!files || files.length === 0) return [];

  console.log(
    `✅ SUPABASE SERVICE: Adding ${files.length} attachments to task ${taskId}`
  );

  try {
    // First, check if the task_attachments table exists
    const { error: checkError } = await supabase
      .from("task_attachments")
      .select("id")
      .limit(1);

    // If the table doesn't exist, fall back to the old method
    if (checkError && checkError.code === "42P01") {
      console.log(
        "⚠️ SUPABASE SERVICE: task_attachments table doesn't exist yet, using fallback method"
      );

      // If there's at least one file, update the task with the first file's info
      if (files.length > 0) {
        await updateTask(taskId, {
          file_url: files[0].url,
          file_name: files[0].name,
        });

        return [
          {
            id: "legacy",
            task_id: taskId,
            file_url: files[0].url,
            file_name: files[0].name,
          },
        ];
      }

      return [];
    }

    // If the table exists, proceed with normal insertion
    const attachments = files.map((file, index) => ({
      task_id: taskId,
      file_url: file.url,
      file_name: file.name,
    }));

    const { data, error } = await supabase
      .from("task_attachments")
      .insert(attachments)
      .select();

    if (error) {
      console.error(
        "❌ SUPABASE SERVICE: Error inserting task attachments:",
        error
      );
      return [];
    }

    console.log(
      `✅ SUPABASE SERVICE: Added ${data.length} attachments successfully`
    );
    return data;
  } catch (error) {
    console.error("❌ SUPABASE SERVICE: Unexpected error:", error);
    return [];
  }
}

export async function fetchTaskAttachments(taskId) {
  console.log(`✅ SUPABASE SERVICE: Fetching attachments for task ${taskId}`);

  try {
    // First, check if the task_attachments table exists
    const { error: checkError } = await supabase
      .from("task_attachments")
      .select("id")
      .limit(1);

    // If the table doesn't exist, fall back to the old method
    if (checkError && checkError.code === "42P01") {
      console.log(
        "⚠️ SUPABASE SERVICE: task_attachments table doesn't exist yet, using fallback method"
      );

      // Get the task to check if it has a file
      const { data: taskData } = await supabase
        .from("tasks")
        .select("file_url, file_name")
        .eq("id", taskId)
        .single();

      if (taskData && taskData.file_url && taskData.file_name) {
        return [
          {
            id: "legacy",
            task_id: taskId,
            file_url: taskData.file_url,
            file_name: taskData.file_name,
            created_at: new Date().toISOString(),
          },
        ];
      }

      return [];
    }

    // If the table exists, proceed with normal fetch
    const { data, error } = await supabase
      .from("task_attachments")
      .select("*")
      .eq("task_id", taskId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error(
        "❌ SUPABASE SERVICE: Error fetching task attachments:",
        error
      );
      return [];
    }

    console.log(
      `✅ SUPABASE SERVICE: Fetched ${data.length} attachments for task ${taskId}`
    );
    return data;
  } catch (error) {
    console.error("❌ SUPABASE SERVICE: Unexpected error:", error);
    return [];
  }
}

export async function deleteTaskAttachment(attachmentId) {
  console.log(`✅ SUPABASE SERVICE: Deleting attachment ${attachmentId}`);

  try {
    // Handle legacy attachments
    if (attachmentId === "legacy") {
      console.log(
        "⚠️ SUPABASE SERVICE: Cannot delete legacy attachment directly"
      );
      return false;
    }

    // First, check if the task_attachments table exists
    const { error: checkError } = await supabase
      .from("task_attachments")
      .select("id")
      .limit(1);

    // If the table doesn't exist, we can't delete
    if (checkError && checkError.code === "42P01") {
      console.log(
        "⚠️ SUPABASE SERVICE: task_attachments table doesn't exist yet"
      );
      return false;
    }

    // If the table exists, proceed with normal deletion
    const { data, error } = await supabase
      .from("task_attachments")
      .delete()
      .eq("id", attachmentId)
      .select();

    if (error) {
      console.error("❌ SUPABASE SERVICE: Error deleting attachment:", error);
      return false;
    }

    console.log(`✅ SUPABASE SERVICE: Attachment deleted successfully`);
    return true;
  } catch (error) {
    console.error("❌ SUPABASE SERVICE: Unexpected error:", error);
    return false;
  }
}

export async function runMigration() {
  console.log("🔄 SUPABASE SERVICE: Running task attachments migration...");

  try {
    // Create a SQL function in Supabase that can execute our migration
    const createFunctionSQL = `
      CREATE OR REPLACE FUNCTION create_task_attachments_table()
      RETURNS BOOLEAN AS $$
      BEGIN
        -- Create the task_attachments table if it doesn't exist
        CREATE TABLE IF NOT EXISTS task_attachments (
          id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
          task_id UUID NOT NULL REFERENCES tasks(id) ON DELETE CASCADE,
          file_url TEXT NOT NULL,
          file_name TEXT NOT NULL,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
          updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
        );
        
        -- Enable Row Level Security
        ALTER TABLE task_attachments ENABLE ROW LEVEL SECURITY;
        
        -- Create RLS policies
        DO $$
        BEGIN
          -- Check if policies exist before creating them
          IF NOT EXISTS (
            SELECT 1 FROM pg_policies 
            WHERE tablename = 'task_attachments' AND policyname = 'Enable read access for all users'
          ) THEN
            CREATE POLICY "Enable read access for all users" ON task_attachments
              FOR SELECT USING (true);
          END IF;
          
          IF NOT EXISTS (
            SELECT 1 FROM pg_policies 
            WHERE tablename = 'task_attachments' AND policyname = 'Enable insert for all users'
          ) THEN
            CREATE POLICY "Enable insert for all users" ON task_attachments
              FOR INSERT WITH CHECK (true);
          END IF;
          
          IF NOT EXISTS (
            SELECT 1 FROM pg_policies 
            WHERE tablename = 'task_attachments' AND policyname = 'Enable update for all users'
          ) THEN
            CREATE POLICY "Enable update for all users" ON task_attachments
              FOR UPDATE USING (true);
          END IF;
          
          IF NOT EXISTS (
            SELECT 1 FROM pg_policies 
            WHERE tablename = 'task_attachments' AND policyname = 'Enable delete for all users'
          ) THEN
            CREATE POLICY "Enable delete for all users" ON task_attachments
              FOR DELETE USING (true);
          END IF;
        END $$;
        
        -- Create index for faster lookups
        CREATE INDEX IF NOT EXISTS idx_task_attachments_task_id ON task_attachments(task_id);
        
        RETURN TRUE;
      EXCEPTION
        WHEN OTHERS THEN
          RAISE NOTICE 'Error creating task_attachments table: %', SQLERRM;
          RETURN FALSE;
      END;
      $$ LANGUAGE plpgsql SECURITY DEFINER;
    `;

    // First create the function
    const { error: createFunctionError } = await supabase
      .rpc("exec_sql", { sql: createFunctionSQL })
      .single();

    if (createFunctionError) {
      // If the exec_sql function doesn't exist, we need a different approach
      console.log(
        "⚠️ SUPABASE SERVICE: exec_sql function not available, trying direct table creation"
      );

      // Try direct table creation with minimal SQL
      const { data, error } = await supabase
        .from("task_attachments")
        .insert({
          task_id: "00000000-0000-0000-0000-000000000000",
          file_url: "https://example.com/placeholder",
          file_name: "placeholder.txt",
        })
        .select();

      if (!error) {
        // If successful, clean up the placeholder
        await supabase.from("task_attachments").delete().eq("id", data[0].id);

        console.log(
          "✅ SUPABASE SERVICE: Task attachments table created successfully!"
        );
        return { success: true };
      }

      console.error(
        "❌ SUPABASE SERVICE: Migration failed:",
        createFunctionError
      );
      return { success: false, error: createFunctionError.message };
    }

    // Then call the function
    const { data, error } = await supabase.rpc("create_task_attachments_table");

    if (error) {
      console.error("❌ SUPABASE SERVICE: Migration failed:", error);
      return { success: false, error: error.message };
    }

    console.log(
      "✅ SUPABASE SERVICE: Task attachments migration completed successfully!"
    );
    return { success: true };
  } catch (error) {
    console.error(
      "❌ SUPABASE SERVICE: Unexpected error during migration:",
      error
    );
    return { success: false, error: error.message };
  }
}

export function getFileUrl(path, bucket = "task-attachments") {
  if (!path) return null;

  console.log(
    `✅ SUPABASE SERVICE: Getting public URL for "${path}" in bucket "${bucket}"`
  );
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);

  return data.publicUrl;
}
